import { useRef, useCallback } from 'react'
import { float32ToInt16 } from '@/utils/audio'
import { useJoinStore } from '@/store/joinStore'

type STTMessage =
  | { type: 'ready'; session_id: string }
  | { type: 'restarted' }
  | { type: 'partial'; text: string }
  | { type: 'final_transcript'; text: string; segment: string }
  | { type: 'reaction_ready'; segment: string; duration_ms: number; success: boolean }
  | { type: 'error'; message: string }

interface UseJoinRealtimeSTTOptions {
  sessionId: string
  onPartialTranscript?: (text: string) => void
  onFinalTranscript?: (segment: string, text: string) => void
  onReactionReady?: (segment: string, durationMs: number) => void
  onError?: (msg: string) => void
}

const MAX_RECONNECT = 3

// Public-flow STT hook. Mirrors useRealtimeSTT (candidate flow) but
// authenticates with the short-lived public bearer from joinStore and
// targets /ws/v1/public/realtime-stt/{sess_id}.
export function useJoinRealtimeSTT({
  sessionId,
  onPartialTranscript,
  onFinalTranscript,
  onReactionReady,
  onError,
}: UseJoinRealtimeSTTOptions) {
  const generationRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reactionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelConnectRef = useRef<(() => void) | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const reactionResolver = useRef<
    ((val: { segment: string; durationMs: number }) => void) | null
  >(null)
  const reconnectAttempts = useRef(0)
  const intentionalClose = useRef(false)
  const startAudioRef = useRef<() => void>(() => {})

  const connect = useCallback(
    (stream: MediaStream): Promise<void> => {
      const generation = ++generationRef.current
      cancelConnectRef.current?.()
      streamRef.current = stream
      intentionalClose.current = false
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext({ sampleRate: 16000 })
      }
      return new Promise((resolve, reject) => {
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const bearer = useJoinStore.getState().bearer
        if (!bearer) {
          reject(new Error('No public bearer in store'))
          return
        }
        let wsUrl =
          `${protocol}://${window.location.host}` +
          `/ws/v1/public/realtime-stt/${sessionId}` +
          `?bearer=${encodeURIComponent(bearer)}`

        const connectionId = useJoinStore.getState().connectionId
        if (connectionId) wsUrl += '&connectionId=' + encodeURIComponent(connectionId)
        const ws = new WebSocket(wsUrl)
        const current = () => generationRef.current === generation && wsRef.current === ws && !intentionalClose.current
        wsRef.current = ws

        const timeout = setTimeout(() => {
          // Close the half-open socket. Without this it can still finish the
          // handshake later and stream audio as a zombie connection after
          // the caller already decided to continue without STT.
          intentionalClose.current = true
          try { ws.close() } catch { /* noop */ }
          if (wsRef.current === ws) wsRef.current = null
          reject(new Error('STT WebSocket connection timeout'))
        }, 5000)

        cancelConnectRef.current = () => {
          clearTimeout(timeout)
          reject(new Error('STT connection cancelled'))
        }
        ws.onopen = () => {
          if (!current()) { ws.close(); return }
          clearTimeout(timeout)
          reconnectAttempts.current = 0
          resolve()
        }
        ws.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('STT WebSocket error'))
        }
        ws.onmessage = (event) => {
          if (!current()) return
          try {
            const msg: STTMessage = JSON.parse(event.data)
            if (msg.type === 'partial') onPartialTranscript?.(msg.text)
            if (msg.type === 'final_transcript')
              onFinalTranscript?.(msg.segment, msg.text)
            if (msg.type === 'reaction_ready') {
              if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current)
              reactionResolver.current?.({
                segment: msg.segment,
                durationMs: msg.duration_ms,
              })
              reactionResolver.current = null
              onReactionReady?.(msg.segment, msg.duration_ms)
            }
            if (msg.type === 'error') onError?.(msg.message)
          } catch {
            /* ignore non-JSON binary frames */
          }
        }
        ws.onclose = () => {
          clearTimeout(timeout)
          if (!current()) return
          reject(new Error('STT connection closed'))
          // Resolve any pending reaction with fallback duration so the
          // caller doesn't hang forever on a network blip.
          reactionResolver.current?.({ segment: '', durationMs: 6000 })
          reactionResolver.current = null

          if (
            !intentionalClose.current &&
            reconnectAttempts.current < MAX_RECONNECT &&
            streamRef.current
          ) {
            const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 10000)
            reconnectAttempts.current++
            reconnectTimerRef.current = setTimeout(() => {
              if (current() && streamRef.current) {
                connect(streamRef.current)
                  .then(() => startAudioRef.current())
                  .catch(() => {
                    /* give up after MAX_RECONNECT */
                  })
              }
            }, delay)
          }
        }
      })
    },
    [sessionId, onPartialTranscript, onFinalTranscript, onReactionReady, onError]
  )

  const startAudio = useCallback(() => {
    const ws = wsRef.current
    const stream = streamRef.current
    const ctx = audioContextRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !stream || !ctx) return
    if (processorRef.current) return

    const source = ctx.createMediaStreamSource(stream)
    sourceRef.current = source
    const processor = ctx.createScriptProcessor(4096, 1, 1)
    processorRef.current = processor

    processor.onaudioprocess = (e) => {
      if (wsRef.current !== ws || intentionalClose.current || ws.readyState !== WebSocket.OPEN) return
      if (ws.bufferedAmount > 65536) return
      const float32 = e.inputBuffer.getChannelData(0)
      const int16 = float32ToInt16(float32)
      ws.send(int16.buffer as any)
    }
    source.connect(processor)
    processor.connect(ctx.destination)
  }, [])

  startAudioRef.current = startAudio

  const stopAudio = useCallback(() => {
    const processor = processorRef.current
    const source = sourceRef.current
    processorRef.current = null
    sourceRef.current = null
    try { processor?.disconnect() } catch { /* already stopped */ }
    try { source?.disconnect() } catch { /* already stopped */ }
  }, [])

  const sendStopAndReact = useCallback(
    (
      segment: string,
      questionContext: string,
      nextContext: string,
      nextTtsText?: string,
    ): Promise<{ segment: string; durationMs: number }> => {
      const generation = generationRef.current
      stopAudio()
      const reactionPromise = new Promise<{
        segment: string
        durationMs: number
      }>((resolve) => {
        reactionResolver.current = resolve
        wsRef.current?.send(
          JSON.stringify({
            type: 'stop_and_react',
            segment,
            question_context: questionContext,
            next_context: nextContext,
            next_tts_text: nextTtsText ?? '',
          }),
        )
      })
      reactionTimerRef.current = setTimeout(() => {
        reactionResolver.current?.({ segment, durationMs: 6000 })
        reactionResolver.current = null
      }, 30_000)
      return reactionPromise.finally(() => {
        if (generationRef.current === generation && reactionTimerRef.current) clearTimeout(reactionTimerRef.current)
      })
    },
    [stopAudio],
  )

  const disconnect = useCallback(() => {
    intentionalClose.current = true
    generationRef.current++
    cancelConnectRef.current?.()
    cancelConnectRef.current = null
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current)
    reactionResolver.current?.({ segment: '', durationMs: 0 })
    reactionResolver.current = null
    reconnectAttempts.current = 0
    stopAudio()
    const ctx = audioContextRef.current
    const ws = wsRef.current
    audioContextRef.current = null
    wsRef.current = null
    streamRef.current = null
    try { void ctx?.close().catch(() => {}) } catch { /* already closed */ }
    try { ws?.close() } catch { /* already closed */ }
  }, [stopAudio])

  return {
    connect,
    startAudio,
    stopAudio,
    sendStopAndReact,
    disconnect,
    wsRef,
  }
}
